import { useState, useEffect, useCallback } from 'react';
import { Box, FormHelperText, Grid, FormControl } from '@mui/material';

import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { clientService } from '../../services/clients';

import { clientActions } from '../../store/actions/client.actions';
import { teamActions } from '../../store/actions/team.actions';

import { useIntercomComponentOnOpenEventTrack } from '../../hooks/useIntercomComponentOnOpenEventTrack';

import { TeamSelect } from '../Teams/TeamSelect';

import { ModalUI } from '../UI/Modal/Modal';
import { ButtonUi } from '../UI/Button/Button';
import { TextFieldUi } from '../UI/TextField/TextField';
import { deleteConfirmationStyle } from '../UI/SimpleModalContent/style';
import SimpleModalContent from '../UI/SimpleModalContent/SimpleModalContent';

import { createClientFormStyle } from './style';

const CreateClientForm = (props) => {
  const {
    getAllTeams,
    teams,
    createClient,
    authentication,
    closeModal,
    onSuccess,
  } = props;
  const { createClientForm } = createClientFormStyle;
  const { deleteConfirmation } = deleteConfirmationStyle;
  const { t } = useTranslation();

  const [name, setName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [teamError, setTeamError] = useState(null);
  const [selectedTeamId, setSelectedTeamId] = useState();
  const [existsClientConfirmationOpen, setExistsClientConfirmationOpen] =
    useState(false);

  const { teams: teamsFromAuth } = authentication;
  const teamsToShow = teams?.teams || [];
  const showTeamErrorMessage = submitted && teamError;

  /**
   * Sets value to the name textfield and reset submitted form state
   * @param event native event
   */
  const onNameInputChange = useCallback((event) => {
    setName(event.target.value);
    setSubmitted(false);
  }, []);

  /**
   * Sets value to the autocomplete
   * @param value selected value
   */
  const onSelect = useCallback((value) => {
    setSubmitted(false);
    if (!value || !value.value) {
      setSelectedTeamId(null);
      return;
    }
    setSelectedTeamId(value.value);
  }, []);

  const onCloseConfirmationModal = () => setExistsClientConfirmationOpen(false);

  const clientExistsCheck = async () => {
    const filter = {
      where: {
        name: { eq: name },
      },
      fields: ['id', 'name'],
    };

    try {
      const existingClient = await clientService.getAllClients(
        `filter=${encodeURIComponent(JSON.stringify(filter))}`,
      );
      if (existingClient?.data.length > 0) return true;
      else return false;
    } catch (error) {
      console.error('[Error]: ', error);
    }
  };

  const handleClientCreation = async () => {
    onCloseConfirmationModal();

    const firstAuthTeam = teamsFromAuth?.[0];
    const fallbackTeamId =
      teamsToShow?.[0]?.id ??
      (typeof firstAuthTeam === 'object' ? firstAuthTeam?.id : firstAuthTeam) ??
      null;

    const actualTeamId = selectedTeamId ?? fallbackTeamId; 

    const createdClient = await createClient({
      name,
      teamId: selectedTeamId || actualTeamId,
    });
    if (onSuccess) {
      onSuccess(createdClient);
    }
    closeModal();
  };

  /**
   * Creates client on form submition
   * @param event native submit event
   */
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedTeamId && teamsToShow?.length > 1) {
      setSubmitted(true);
      setTeamError(t('errors.select.team'));
      return;
    }
    const clientExists = await clientExistsCheck();
    if (clientExists) setExistsClientConfirmationOpen(true);
    else handleClientCreation();
  };

  useIntercomComponentOnOpenEventTrack('start-client-creation');

  /**
   * Fetches all teams if they are not defined
   */
  useEffect(() => {
    getAllTeams();
  }, [getAllTeams]);

  return (
    <>
      <Box
        component='form'
        className='create-form validate-form'
        onSubmit={handleSubmit}
      >
        <Grid container sx={createClientForm}>
          {teamsToShow.length > 1 && (
            <Grid item xs={12} sm={12}>
              <FormControl fullWidth>
                <TeamSelect
                  handleChange={onSelect}
                  selectedId={selectedTeamId}
                />
                {showTeamErrorMessage && (
                  <FormHelperText error>{teamError}</FormHelperText>
                )}
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} sm={12}>
            <TextFieldUi
              className='simple-text-field'
              label={t('forms.client_name_with_example')}
              autoFocus
              name='name'
              fullWidth
              required
              value={name}
              handleChange={onNameInputChange}
            />
          </Grid>
        </Grid>
        <Box className='modal-footer justify-end'>
          <ButtonUi
            className='action-btn accept'
            variant='contained'
            type='primary'
            size='small'
            btnType='submit'
          >
            {t('buttons.create.client')}
          </ButtonUi>
        </Box>
      </Box>
      <ModalUI
        modalSx={deleteConfirmation}
        open={existsClientConfirmationOpen}
        isCloseBtn={false}
        onCloseHandler={onCloseConfirmationModal}
        className='confirmation-modal'
        modalTitle={t('alerts.titles.create')}
      >
        <SimpleModalContent
          content={['alerts.confirm.duplicate-client-creation']}
          mainBtn='buttons.confirm'
          onMainBtnClick={handleClientCreation}
          secondaryBtn='buttons.cancel'
          onSecondaryBtnClick={onCloseConfirmationModal}
        />
      </ModalUI>
    </>
  );
};

const mapDispatchToProps = {
  createClient: clientActions.createClient,
  getAllTeams: teamActions.getAllTeamsWithoutMeta,
};

const mapStateToProps = (state) => ({
  ...state,
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateClientForm);
